import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building, User, Briefcase, FileText, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useCompany } from '@/contexts/CompanyContext';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { CandidateNameLink } from '@/components/candidates/CandidateNameLink';
import {  formatFullName, filterCandidatesBySearch, filterCompaniesBySearch, filterPositionsBySearch, filterByText, type TranscriptEntry } from '@/lib/utils';

interface SearchResult {
  id: string;
  type: 'company' | 'candidate' | 'position' | 'interview' | 'transcript';
  title: string;
  subtitle?: string;
  metadata?: string;
  route: string;
  candidateId?: string;
  candidateName?: string;
}

const GlobalSearch: React.FC = () => {
  const { tenantId, isTenantInterviewer } = useAuth();
  const { companies, setSelectedCompany } = useCompany();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Handle clicks outside search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!query.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      performSearch();
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, tenantId]);

  const performSearch = async () => {
    if (!tenantId || !query.trim()) return;

    setLoading(true);
    console.log('Searching for:', query, 'with tenantId:', tenantId);

    try {
      const allResults: SearchResult[] = [];

      // Search companies - fetch all and filter client-side with token-based matching
      const { data: allCompanies, error: companiesError } = await supabase
        .from('companies')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .neq('status', 'archived');

      console.log('Companies search result:', allCompanies, 'Error:', companiesError);

      if (allCompanies) {
        // Apply token-based filtering
        const filteredCompanies = filterCompaniesBySearch(allCompanies, query).slice(0, 5);
        
        allResults.push(
          ...filteredCompanies.map(company => ({
            id: company.id,
            type: 'company' as const,
            title: company.name,
            subtitle: 'Company',
            metadata: undefined,
            // Interviewers should go to positions page (filtered by company), not the edit page
            route: isTenantInterviewer ? `/positions` : `/companies/${company.id}/edit`,
          }))
        );
      }

      // Search candidates - fetch all and filter client-side with token-based matching
      const { data: allCandidates, error: candidatesError } = await supabase
        .from('candidates')
        .select('id, first_name, last_name, email')
        .eq('tenant_id', tenantId);

      console.log('Candidates search result:', allCandidates, 'Error:', candidatesError);

      if (allCandidates) {
        // Apply token-based filtering - type inferred from allCandidates
        const filteredCandidates = filterCandidatesBySearch(allCandidates, query).slice(0, 5);
        
        allResults.push(
          ...filteredCandidates.map(candidate => ({
            id: candidate.id,
            type: 'candidate' as const,
            title: formatFullName(candidate.first_name, candidate.last_name),
            subtitle: candidate.email,
            metadata: undefined,
            route: `/candidates/${candidate.id}`,
          }))
        );
      }

      // Search positions - fetch all and filter client-side with token-based matching
      const { data: allPositions, error: positionsError } = await supabase
        .from('positions')
        .select(
          `
          id, 
          title, 
          role_overview,
          company_id,
          companies(id, name)
        `
        )
        .eq('tenant_id', tenantId);

      console.log('Positions search result:', allPositions, 'Error:', positionsError);

      if (allPositions) {
        // Apply token-based filtering - type inferred from allPositions
        const filteredPositions = filterPositionsBySearch(allPositions, query).slice(0, 5);
        
        allResults.push(
          ...filteredPositions.map(position => ({
            id: position.id,
            type: 'position' as const,
            title: position.title,
            subtitle: position.companies?.name || 'Position',
            metadata: undefined,
            route: `/positions/${position.id}`,
          }))
        );
      }

      // Search interview sessions
      const { data: interviews, error: interviewsError } = await supabase
        .from('interview_sessions')
        .select(
          `
          id,
          start_time,
          status,
          candidate_id,
          position_id,
          candidates(id, first_name, last_name),
          positions(id, title)
        `
        )
        .eq('tenant_id', tenantId)
        .limit(10);

      console.log('Interviews search result:', interviews, 'Error:', interviewsError);

      if (interviews) {
        const filteredInterviews = interviews.filter(interview => {
          const candidate = (interview as any).candidates;
          const position = (interview as any).positions;
          
          if (candidate) {
            const candidateMatches = filterCandidatesBySearch([candidate], query).length > 0;
            if (candidateMatches) return true;
          }
          
          if (position) {
            const positionMatches = filterPositionsBySearch([position], query).length > 0;
            if (positionMatches) return true;
          }
          
          return false;
        });

        allResults.push(
          ...filteredInterviews.slice(0, 5).map(interview => {
            const candidate = (interview as any).candidates;
            const position = (interview as any).positions;
            const candidateName = formatFullName(candidate?.first_name, candidate?.last_name);

            return {
              id: interview.id,
              type: 'interview' as const,
              title: `Interview: ${candidateName}`,
              subtitle: position?.title || 'Interview',
              metadata: interview.start_time
                ? format(new Date(interview.start_time), 'MMM d, yyyy h:mm a')
                : interview.status,
              route: `/positions/${interview.position_id}?candidateId=${interview.candidate_id}&sessionId=${interview.id}&showAssessment=true#candidates`,
              candidateId: interview.candidate_id,
              candidateName: candidateName,
            };
          })
        );
      }

      // Search transcripts (through interview sessions with transcript entries)
      const { data: allTranscripts } = await supabase
        .from('transcript_entries')
        .select(
          `
          id,
          text,
          session_id,
          interview_sessions!session_id(
            id,
            candidate_id,
            position_id,
            candidates(id, first_name, last_name),
            positions(id, title)
          )
        `
        )
        .eq('tenant_id', tenantId)
        .limit(50);

      if (allTranscripts && allTranscripts.length > 0) {
        // Use imported TranscriptEntry type
        const matchingTranscripts = filterByText(allTranscripts as TranscriptEntry[], query, entry => entry.text);
        
        const sessionMap = new Map();
        matchingTranscripts.forEach(entry => {
          const session = (entry as any).interview_sessions;
          if (session && !sessionMap.has(session.id)) {
            sessionMap.set(session.id, session);
          }
        });

        const uniqueSessions = Array.from(sessionMap.values());
        allResults.push(
          ...uniqueSessions.slice(0, 5).map(session => {
            const candidateName = formatFullName(
              session.candidates?.first_name,
              session.candidates?.last_name
            );

            return {
              id: session.id,
              type: 'transcript' as const,
              title: `Transcript: ${candidateName}`,
              subtitle: session.positions?.title || 'Transcript',
              metadata: 'Contains matching text',
              route: `/positions/${session.position_id}?candidateId=${session.candidate_id}&sessionId=${session.id}&showTranscript=true#candidates`,
              candidateId: session.candidate_id,
              candidateName: candidateName,
            };
          })
        );
      }

      console.log('Total search results:', allResults.length, allResults);
      setResults(allResults);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    // For company results clicked by interviewers, set the selected company
    // so the positions page will be filtered to that company
    if (result.type === 'company' && isTenantInterviewer) {
      const company = companies.find(c => c.id === result.id);
      if (company) {
        setSelectedCompany(company);
      }
    }

    navigate(result.route);
    setQuery('');
    setShowResults(false);
  };

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'company':
        return Building;
      case 'candidate':
        return User;
      case 'position':
        return Briefcase;
      case 'interview':
        return Calendar;
      case 'transcript':
        return FileText;
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'company':
        return 'Company';
      case 'candidate':
        return 'Candidate';
      case 'position':
        return 'Position';
      case 'interview':
        return 'Interview';
      case 'transcript':
        return 'Transcript';
    }
  };

  // Group results by type
  const groupedResults = results.reduce(
    (acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    },
    {} as Record<SearchResult['type'], SearchResult[]>
  );

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search companies, candidates, positions..."
          className="h-9 w-full bg-background pl-8 md:w-[300px] lg:w-[400px]"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => query.trim() && results.length > 0 && setShowResults(true)}
        />
      </div>

      {showResults && (
        <Card className="absolute top-full mt-2 w-full max-h-[500px] overflow-auto shadow-lg z-50">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Searching...</div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No results found for "{query}"
              </div>
            ) : (
              <div className="py-2">
                {Object.entries(groupedResults).map(([type, typeResults]) => (
                  <div key={type} className="mb-2 last:mb-0">
                    <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
                      {getTypeLabel(type as SearchResult['type'])}s
                    </div>
                    {typeResults.map(result => {
                      const Icon = getIcon(result.type);
                      return (
                        <button
                          key={`${result.type}-${result.id}`}
                          className="w-full px-3 py-2 hover:bg-accent/50 transition-colors text-left"
                          onClick={() => handleResultClick(result)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">
                                {(result.type === 'interview' || result.type === 'transcript') &&
                                result.candidateId &&
                                result.candidateName ? (
                                  <>
                                    {result.type === 'interview' ? 'Interview: ' : 'Transcript: '}
                                    <CandidateNameLink
                                      candidateId={result.candidateId || null}
                                      candidateName={result.candidateName || null}
                                    />
                                  </>
                                ) : (
                                  result.title
                                )}
                              </div>
                              {result.subtitle && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {result.subtitle}
                                </div>
                              )}
                              {result.metadata && (
                                <div className="text-xs text-muted-foreground/70 truncate mt-0.5">
                                  {result.metadata}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GlobalSearch;
